// =============================================================================
// CRM Lookup — BaseQL operation (shared layer: imported by BOTH bundles)
// =============================================================================
// Target path: entrypoints/zwickroell/src/db/crm-lookup-resolvers.ts
//
// 1 operation:
//   - crmLookup (query) — looks a caller up in the CRM via the MCP wrapper
//     (phone / case number / machine number / name) and returns the
//     serialized CrmLookupResult as a JSON STRING (type () => String keeps
//     BaseQL typing trivial — same JSON-as-String convention as
//     testLabListCallers / systemHealth / getPricingConfig in
//     src/db/test-lab-resolvers.ts).
//
// SERVER-IMPORT STYLE — deliberate, copied from src/db/test-lab-resolvers.ts:
// that file imports '../server/test-lab/test-token-store.js' etc. at TOP LEVEL
// from a shared db/ file. The adornis-cli client build stubs every module
// under a `/server/` path segment to an empty module (verified: 0 occurrences
// of "mongodb" in dist/client/startup.js), so a plain static import is the
// app's canonical guard — NO dynamic `await import(...)` needed. The imported
// symbol is `undefined` on the client, which is harmless because resolve()
// only ever executes server-side (on the client, registerQuery returns a
// GraphQL request function instead).
//
// ERROR CONTRACT: the resolver NEVER throws for lookup failures — everything
// (including auth) is wrapped in a top-level try/catch that returns
// JSON.stringify({ ok: false, error: String(err), inputType, notes: [] }) —
// the SAME envelope shape the server orchestrator emits on its own ok:false
// paths, so the panel only ever sees one dialect. The client therefore
// always receives parseable JSON in `value`; do NOT rely on useQuery().error
// for domain errors (it only fires on transport-level failures).
//
// -----------------------------------------------------------------------------
// CLIENT CALL SHAPE (exact registerQuery invocation convention of this app —
// POSITIONAL args matching `params` order, curried double call, EMPTY second
// call because the return type is the scalar String, so no selection set):
//
//   import { crmLookup } from '../../db/crm-lookup-resolvers.js';
//
//   // imperative (e.g. inside a click handler):
//   const raw = (await crmLookup(inputType, value, accountNumber ?? '')()) as unknown as string;
//   const data = JSON.parse(raw);            // CrmLookupResult | { ok:false, error, inputType, notes }
//
//   // reactive, with useQuery (deps array is REQUIRED or useQuery throws):
//   const lookupQuery = useQuery(
//     () => (value ? crmLookup(inputType, value, accountNumber ?? '')() : Promise.resolve(null)),
//     [inputType, value, accountNumber],
//   );
//   const data = lookupQuery.value ? JSON.parse(lookupQuery.value as unknown as string) : null;
//
// NOTE: args are POSITIONAL — `crmLookup({ inputType, value })` would be WRONG
// in this codebase; the first call maps its arguments 1:1 onto the `params`
// array (see testLabRequestTestSession / testLabSaveFeedback call sites in
// TestLabPage.ts / CallPage.ts). Optional accountNumber follows the app's
// empty-string convention (cf. testLabHistory's profileId): pass '' when absent.
//
// REGISTRATION: add to src/server/startup.ts (top level, next to the existing
// test-lab-resolvers import, BEFORE `new SchemaBuilder().generateSchema()`):
//   import '../db/crm-lookup-resolvers.js'; // CRM Lookup — side-effect registers BaseQL op before schema regen
// New op = schema change -> restart the node server (late registration throws
// "sealed"). Client side needs no wiring beyond importing `crmLookup` from the
// consuming component.
// =============================================================================

import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getContext } from '@adornis/baseql/server/context.js'; // emptied on client by build shim
import { logger } from '@adornis/base/logging.js';
// Server orchestrator — top-level import on purpose (matches test-lab-resolvers.ts
// style; `/server/` files become empty modules in the client bundle). The server
// exports `crmLookup(inputType: string, value: string, accountNumber?: string):
// Promise<CrmLookupResult>` (POSITIONAL args) — imported under the
// `performCrmLookup` alias to avoid colliding with this file's own `crmLookup`
// registered-query export below.
import { crmLookup as performCrmLookup } from '../server/crm-lookup.js'; // emptied on client

// ─── auth helper (same pattern as test-lab-resolvers.ts) ──────────────────
function requireAuthFromContext(): string {
  const ctx = getContext();
  const userID = (ctx as any)?.userID;
  if (!userID) throw new Error('AUTH_REQUIRED: please sign in');
  return userID;
}

// ─── allowed inputType discriminators (validated server-side) ─────────────
// Must stay aligned with VALID_INPUT_TYPES in src/server/crm-lookup.ts and
// the panel's search-type chips: phone | case | machine | name.
const ALLOWED_INPUT_TYPES = new Set(['phone', 'case', 'machine', 'name']);

// ─── crmLookup (query) ────────────────────────────────────────────────────
// Looks up a caller/account in the CRM. Returns the serialized CrmLookupResult
// (JSON string). Domain + infrastructure errors are returned IN-BAND as
// { ok: false, error, inputType, notes: [] } — never thrown — so the client
// always gets JSON in the server's own envelope shape.
export const crmLookup = registerQuery({
  type: () => String,
  operationName: 'crmLookup',
  resolve: (inputType: string, value: string, accountNumber: string) => async () => {
    // Normalized OUTSIDE the try so the catch block can echo it in the envelope.
    const type = (inputType ?? '').trim();
    try {
      const userID = requireAuthFromContext();

      const val = (value ?? '').trim();
      // Optional param, app convention: empty string means "not provided"
      // (cf. profileId in testLabHistory). Normalize '' -> undefined here.
      const acct = (accountNumber ?? '').trim() || undefined;

      if (!ALLOWED_INPUT_TYPES.has(type)) {
        return JSON.stringify({
          ok: false,
          error: `INVALID_INPUT_TYPE: must be one of ${[...ALLOWED_INPUT_TYPES].join(', ')}`,
          inputType: type,
          notes: [],
        });
      }
      if (val === '') {
        return JSON.stringify({
          ok: false,
          error: 'VALUE_REQUIRED: lookup value must not be empty',
          inputType: type,
          notes: [],
        });
      }

      // POSITIONAL call — matches the server export's signature exactly.
      const result = await performCrmLookup(type, val, acct);

      logger.info({ userID, inputType: type }, 'CRM lookup performed');

      // Orchestrator returns the CrmLookupResult object -> serialize here.
      // (Defensive: if it already returns a pre-serialized string, pass through.)
      return typeof result === 'string' ? result : JSON.stringify(result);
    } catch (err) {
      // Top-level guard: ALL failures (auth, MCP timeouts, validation throws
      // inside the orchestrator) come back as parseable JSON, never as a
      // GraphQL error. String(err) keeps Error -> "Error: message" readable.
      // Envelope mirrors the server's own ok:false shape (inputType + notes).
      return JSON.stringify({ ok: false, error: String(err), inputType: type, notes: [] });
    }
  },
  params: [
    { name: 'inputType', type: () => String },
    { name: 'value', type: () => String },
    { name: 'accountNumber', type: () => String }, // optional: pass '' when absent
  ],
});
